import {Controller} from "@hotwired/stimulus";
import * as echarts from 'echarts';

export default class extends Controller {
  static values = {
    data: String,
    breakupUrl: { type: String, default: '' },
  };

  connect() {
    this.loadScripts()
    echarts.registerTheme('infographic', this.chartColor())
    this.chart = echarts.init(this.element, 'infographic');
    const options = window[this.dataValue]

    if (this.isEmpty(options)) {
      this.showNoDataMessage()
      return
    }

    this.initLabelToggle(options)
    this.chart.setOption(options);
    this.chart.on('legendselectchanged', this.handleLegendChange.bind(this));

    if (this.breakupUrlValue && options._breakup && options._breakup.rkeys) {
      this.breakupConfig = options._breakup;
      this.chart.on('click', this.handleBreakupClick.bind(this));
      this.element.style.cursor = 'pointer';
    }
  }

  isEmpty(options) {
    if (!options || Object.keys(options).length === 0) return true;
    let series = options.series;
    if (!series) return true;
    if (!Array.isArray(series)) series = [series];
    if (series.length === 0) return true;

    return series.every(s => !s.data || (Array.isArray(s.data) && s.data.length === 0));
  }

  showNoDataMessage() {
    this.chart.setOption({
      title: {
        show: true,
        textStyle: {
          color: '#9ca3af',
          fontSize: 14,
          fontWeight: 'normal'
        },
        text: 'No data available',
        left: 'center',
        top: 'center'
      },
      xAxis: { show: false },
      yAxis: { show: false },
      series: []
    });
  }

  handleLegendChange(params) {
    const option = this.chart.getOption();
    const series = option.series;
    const totalPosSeries = series.find(s => s.name === 'Total Positive');
    const totalNegSeries = series.find(s => s.name === 'Total Negative');

    if (!totalPosSeries && !totalNegSeries) return;

    const selected = params.selected;
    // Assuming all series have same data length and valid data
    const dataLength = series[0].data.length;
    const totals = new Array(dataLength).fill(0);

    series.forEach(s => {
      // Skip total series and rejected series
      if (s.name === 'Total Positive' || s.name === 'Total Negative' || s.reject) return;

      // Check if series is selected (visible). If key is missing, it's usually selected (default).
      const isSelected = selected[s.name] !== false;

      if (isSelected) {
        s.data.forEach((item, index) => {
          let val = 0;
          if (item && item.value != null) {
            val = parseFloat(item.value);
          } else if (typeof item === 'number') {
            val = item;
          }

          if (!isNaN(val)) {
            totals[index] += val;
          }
        });
      }
    });

    const posTotals = totals.map(t => t > 0 ? (Math.round(t * 100) / 100) : '');
    const negTotals = totals.map(t => t < 0 ? (Math.round(t * 100) / 100) : '');

    // We only update the formatter function for the labels.
      // However, setOption with just series name and label might not be enough if we don't update data,
    // but the previous ruby code set data to 0s and relied on formatter.
    // Here we can update the formatter to return the calculated value from closure.

    this.chart.setOption({
      series: [
        {
          name: 'Total Positive',
          label: {
            formatter: function (params) {
              return posTotals[params.dataIndex];
            }
          }
        },
        {
          name: 'Total Negative',
          label: {
            formatter: function (params) {
              return negTotals[params.dataIndex];
            }
          }
        }
      ]
    });
  }

  initLabelToggle(options) {
    if (options?.toolbox?.feature?.myLabelToggler) {
      options.toolbox.feature.myLabelToggler.onclick = () => {
        const currentOption = this.chart.getOption();
        const isVisible = !!(currentOption.series[0].label && currentOption.series[0].label.show);

        this.chart.setOption({
          series: currentOption.series.map(s => ({
            label: { show: !isVisible }
          }))
        });
      };
    }
  }


  handleBreakupClick(params) {
    if (!this.breakupConfig?.rkeys) return;

    const indexMap = this.breakupConfig.rkeys[params.dataIndex];
    if (!indexMap) return;

    // For bar/line/stacked/mixed: seriesName matches directly
    let entry = indexMap[params.seriesName];

    // For pie: seriesName is the pie series name, use first entry
    if (!entry && params.seriesType === 'pie') {
      entry = Object.values(indexMap)[0];
    }

    if (!entry) return;

    // Drilldown: navigate in-page via Turbo Stream to target chart
    if (typeof entry === 'object' && entry.type === 'drilldown') {
      const urlParams = entry.url_params;
      const orgSlug = window.location.pathname.split('/')[1];
      const base = `/${orgSlug}/s/${urlParams.story_template_slug}/${urlParams.page}/drilldown`;
      const url = new URL(base, window.location.origin);
      url.searchParams.set('page_block_item_id', urlParams.page_block_item_id);
      url.searchParams.set('rkey', entry.rkey);
      // Pass source chart's turbo frame ID so the response replaces the correct frame
      const sourceFrame = this.element.closest('turbo-frame');
      if (sourceFrame) url.searchParams.set('source_frame_id', sourceFrame.id);
      fetch(url.toString(), {
        headers: { 'Accept': 'text/vnd.turbo-stream.html' }
      }).then(r => r.text()).then(html => Turbo.renderStreamMessage(html));
      return;
    }

    // Breakup: open detail rows in new tab
    const url = new URL(this.breakupUrlValue, window.location.origin);
    url.searchParams.set('rkey', entry);
    window.open(url.toString(), '_blank');
  }

  loadScripts() {
    this.element.querySelectorAll('script').forEach(function (content) {
      var script = document.createElement('script');
      script.innerHTML = content.innerHTML;
      document.head.appendChild(script).parentNode.removeChild(script);
    });
  }

  chartColor() {
    var data = {
      "color": [
        "#4C72B0",
        "#C44E52",
        "#55A868",
        "#DAA520",
        "#8172B2",
        "#3398DB",
        "#64B5CD",
        "#E58673",
        "#88D1A3",
        "#FFC400",
        "#B299C8",
        "#A9A9A9",
        "#469990",
        "#D27C2C",
        "#A45D98"
      ],
      "backgroundColor": "rgba(0,0,0,0)",
      "textStyle": {},
      "title": {
        "textStyle": {
          "color": "#3F3F46"
        },
        "subtextStyle": {
          "color": "#737373"
        }
      },
      "line": {
        "itemStyle": {
          "borderWidth": 1
        },
        "lineStyle": {
          "width": "3"
        },
        "symbolSize": "5",
        "symbol": "emptyCircle",
        "smooth": false
      },
      "radar": {
        "itemStyle": {
          "borderWidth": 1
        },
        "lineStyle": {
          "width": "3"
        },
        "symbolSize": "5",
        "symbol": "emptyCircle",
        "smooth": false
      },
      "bar": {
        "itemStyle": {
          "barBorderWidth": 0,
          "barBorderColor": "#ccc"
        }
      },
      "pie": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "scatter": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "boxplot": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "parallel": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "sankey": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "funnel": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "gauge": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        }
      },
      "candlestick": {
        "itemStyle": {
          "color": "#c1232b",
          "color0": "#b5c334",
          "borderColor": "#c1232b",
          "borderColor0": "#b5c334",
          "borderWidth": 1
        }
      },
      "graph": {
        "itemStyle": {
          "borderWidth": 0,
          "borderColor": "#ccc"
        },
        "lineStyle": {
          "width": 1,
          "color": "#aaaaaa"
        },
        "symbolSize": "5",
        "symbol": "emptyCircle",
        "smooth": false,
        "color": [
          "#6b21a8",
          "#27727b",
          "#fcce10",
          "#e87c25",
          "#b5c334",
          "#fe8463",
          "#9bca63",
          "#fad860",
          "#f3a43b",
          "#60c0dd",
          "#d7504b",
          "#c6e579",
          "#f4e001",
          "#f0805a",
          "#26c0c0",
          "#333333"
        ],
        "label": {
          "color": "#eeeeee"
        }
      },
      "map": {
        "itemStyle": {
          "areaColor": "#dddddd",
          "borderColor": "#eeeeee",
          "borderWidth": 0.5
        },
        "label": {
          "color": "#c1232b"
        },
        "emphasis": {
          "itemStyle": {
            "areaColor": "#fe994e",
            "borderColor": "#444",
            "borderWidth": 1
          },
          "label": {
            "color": "rgb(100,0,0)"
          }
        }
      },
      "geo": {
        "itemStyle": {
          "areaColor": "#dddddd",
          "borderColor": "#eeeeee",
          "borderWidth": 0.5
        },
        "label": {
          "color": "#c1232b"
        },
        "emphasis": {
          "itemStyle": {
            "areaColor": "#fe994e",
            "borderColor": "#444",
            "borderWidth": 1
          },
          "label": {
            "color": "rgb(100,0,0)"
          }
        }
      },
      "categoryAxis": {
        "axisLine": {
          "show": true,
          "lineStyle": {
            "color": "#27727b"
          }
        },
        "axisTick": {
          "show": true,
          "lineStyle": {
            "color": "#27727b"
          }
        },
        "axisLabel": {
          "show": true,
          "color": "#333"
        },
        "splitLine": {
          "show": false,
          "lineStyle": {
            "color": [
              "#ccc"
            ]
          }
        },
        "splitArea": {
          "show": false,
          "areaStyle": {
            "color": [
              "rgba(250,250,250,0.3)",
              "rgba(200,200,200,0.3)"
            ]
          }
        }
      },
      "valueAxis": {
        "axisLine": {
          "show": false,
          "lineStyle": {
            "color": "#333"
          }
        },
        "axisTick": {
          "show": false,
          "lineStyle": {
            "color": "#333"
          }
        },
        "axisLabel": {
          "show": true,
          "color": "#333"
        },
        "splitLine": {
          "show": true,
          "lineStyle": {
            "color": [
              "#ccc"
            ]
          }
        },
        "splitArea": {
          "show": false,
          "areaStyle": {
            "color": [
              "rgba(250,250,250,0.3)",
              "rgba(200,200,200,0.3)"
            ]
          }
        }
      },
      "logAxis": {
        "axisLine": {
          "show": true,
          "lineStyle": {
            "color": "#27727b"
          }
        },
        "axisTick": {
          "show": true,
          "lineStyle": {
            "color": "#333"
          }
        },
        "axisLabel": {
          "show": true,
          "color": "#333"
        },
        "splitLine": {
          "show": true,
          "lineStyle": {
            "color": [
              "#ccc"
            ]
          }
        },
        "splitArea": {
          "show": false,
          "areaStyle": {
            "color": [
              "rgba(250,250,250,0.3)",
              "rgba(200,200,200,0.3)"
            ]
          }
        }
      },
      "timeAxis": {
        "axisLine": {
          "show": true,
          "lineStyle": {
            "color": "#27727b"
          }
        },
        "axisTick": {
          "show": true,
          "lineStyle": {
            "color": "#3f3f46"
          }
        },
        "axisLabel": {
          "show": true,
          "color": "#3f3f46"
        },
        "splitLine": {
          "show": true,
          "lineStyle": {
            "color": [
              "#ccc"
            ]
          }
        },
        "splitArea": {
          "show": false,
          "areaStyle": {
            "color": [
              "rgba(250,250,250,0.3)",
              "rgba(200,200,200,0.3)"
            ]
          }
        }
      },
      "toolbox": {
        "iconStyle": {
          "borderColor": "#c1232b"
        },
        "emphasis": {
          "iconStyle": {
            "borderColor": "#e87c25"
          }
        }
      },
      "legend": {
        "textStyle": {
          "color": "#333333"
        }
      },
      "tooltip": {
        "axisPointer": {
          "lineStyle": {
            "color": "#27727b",
            "width": 1
          },
          "crossStyle": {
            "color": "#27727b",
            "width": 1
          }
        }
      },
      "timeline": {
        "lineStyle": {
          "color": "#293c55",
          "width": 1
        },
        "itemStyle": {
          "color": "#27727b",
          "borderWidth": 1
        },
        "controlStyle": {
          "color": "#27727b",
          "borderColor": "#27727b",
          "borderWidth": 0.5
        },
        "checkpointStyle": {
          "color": "#c1232b",
          "borderColor": "#c23531"
        },
        "label": {
          "color": "#293c55"
        },
        "emphasis": {
          "itemStyle": {
            "color": "#72d4e0"
          },
          "controlStyle": {
            "color": "#27727b",
            "borderColor": "#27727b",
            "borderWidth": 0.5
          },
          "label": {
            "color": "#293c55"
          }
        }
      },
      "visualMap": {
        "color": [
          "#6b21a8",
          "#fcce10"
        ]
      },
      "dataZoom": {
        "backgroundColor": "rgba(0,0,0,0)",
        "dataBackgroundColor": "rgba(181,195,52,0.3)",
        "fillerColor": "rgba(181,195,52,0.2)",
        "handleColor": "#27727b",
        "handleSize": "100%",
        "textStyle": {
          "color": "#999999"
        }
      },
      "markPoint": {
        "label": {
          "color": "#eeeeee"
        },
        "emphasis": {
          "label": {
            "color": "#eeeeee"
          }
        }
      }
    }
    return data;
  }

  disconnect() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }


}
